/**
 * MUTAGIM.SHOP — Gift Card Purchase Page (Phase 15)
 * Page d'achat de cartes cadeaux
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Gift, Mail, User, MessageSquare, Check, CreditCard, Sparkles } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Montants prédéfinis
const GIFT_CARD_AMOUNTS = [25, 50, 75, 100, 150, 200];

export function GiftCardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { token } = useAuth();
  
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [giftCard, setGiftCard] = useState(null);

  const finalAmount = useCustom ? parseFloat(customAmount) || 0 : selectedAmount;

  const handlePurchase = async (e) => {
    e.preventDefault();
    
    if (finalAmount < 10 || finalAmount > 500) {
      toast.error(t('giftCard.amountError', 'Le montant doit être entre 10€ et 500€'));
      return;
    }

    if (!recipientEmail) {
      toast.error(t('giftCard.emailRequired', 'Veuillez entrer l\'email du destinataire'));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/promotions/gift-cards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          amount: finalAmount,
          recipient_email: recipientEmail,
          sender_name: senderName || null,
          message: message || null,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to create gift card');
      }

      const data = await res.json();
      setGiftCard(data.gift_card);
      toast.success(t('giftCard.created', 'Carte cadeau créée avec succès !'));
    } catch (err) {
      toast.error(t('common.error', 'Une erreur est survenue'));
      console.error('Gift card error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Affichage après achat réussi
  if (giftCard) {
    return (
      <div className="min-h-screen flex flex-col bg-mutagim-dark">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="max-w-md w-full bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('giftCard.success', 'Carte Cadeau Créée !')}
              </h1>
              
              <p className="text-gray-400 mb-6">
                {t('giftCard.sentTo', 'Un email sera envoyé à')} <span className="text-white">{giftCard.recipient_email}</span>
              </p>

              {/* Gift Card Preview */}
              <div className="relative bg-gradient-to-br from-mutagim-red to-red-700 rounded-xl p-6 mb-6 shadow-lg shadow-mutagim-red/30">
                <div className="absolute top-4 right-4">
                  <Gift className="w-8 h-8 text-white/30" />
                </div>
                <div className="text-left">
                  <p className="text-white/70 text-sm mb-1">MUTAGIM.SHOP</p>
                  <p className="text-white text-3xl font-bold mb-4">
                    {formatPrice({ EUR: giftCard.initial_amount })}
                  </p>
                  <p className="text-white/80 font-mono text-lg tracking-wider">
                    {giftCard.code}
                  </p>
                </div>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                <p className="text-gray-400 text-sm">
                  {t('giftCard.validUntil', 'Valide pendant 1 an')}
                </p>
                <p className="text-white font-mono mt-1">
                  {giftCard.code}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="flex-1 border-gray-600"
                >
                  {t('common.backHome', 'Retour')}
                </Button>
                <Button
                  onClick={() => setGiftCard(null)}
                  className="flex-1 bg-mutagim-red hover:bg-red-700"
                >
                  {t('giftCard.buyAnother', 'Acheter une autre')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="gift-card-page">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-6">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('giftCard.title', 'Cartes Cadeaux Mutagim')}
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            {t('giftCard.subtitle', 'Offrez du style ! La carte cadeau parfaite pour les passionnés de streetwear.')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Left: Amount Selection */}
          <div>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-mutagim-red" />
                  {t('giftCard.selectAmount', 'Choisir le montant')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Predefined amounts */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {GIFT_CARD_AMOUNTS.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => { setSelectedAmount(amount); setUseCustom(false); }}
                      className={cn(
                        "p-4 rounded-lg border-2 text-center transition-all",
                        !useCustom && selectedAmount === amount
                          ? "border-mutagim-red bg-mutagim-red/10 text-white"
                          : "border-gray-700 text-gray-400 hover:border-gray-600"
                      )}
                      data-testid={`amount-${amount}`}
                    >
                      <span className="text-xl font-bold">{amount}€</span>
                    </button>
                  ))}
                </div>

                {/* Custom amount */}
                <div className="relative">
                  <div 
                    className={cn(
                      "p-4 rounded-lg border-2 transition-all",
                      useCustom ? "border-mutagim-red bg-mutagim-red/10" : "border-gray-700"
                    )}
                  >
                    <Label className="text-gray-400 text-sm mb-2 block">
                      {t('giftCard.customAmount', 'Montant personnalisé (10€ - 500€)')}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={10}
                        max={500}
                        value={customAmount}
                        onChange={(e) => { setCustomAmount(e.target.value); setUseCustom(true); }}
                        onFocus={() => setUseCustom(true)}
                        placeholder="100"
                        className="bg-gray-800 border-gray-700 text-white text-xl font-bold"
                      />
                      <span className="text-white text-xl">€</span>
                    </div>
                  </div>
                </div>

                {/* Selected amount display */}
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg border border-purple-500/30">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">{t('giftCard.total', 'Total')}</span>
                    <span className="text-2xl font-bold text-white">
                      {formatPrice({ EUR: finalAmount })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Recipient Details */}
          <div>
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-mutagim-red" />
                  {t('giftCard.recipientDetails', 'Destinataire')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePurchase} className="space-y-4">
                  <div>
                    <Label htmlFor="recipientEmail" className="text-gray-300">
                      {t('giftCard.recipientEmail', 'Email du destinataire')} *
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="recipientEmail"
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        required
                        placeholder="ami@email.com"
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                        data-testid="recipient-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="senderName" className="text-gray-300">
                      {t('giftCard.senderName', 'Votre nom (affiché au destinataire)')}
                    </Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <Input
                        id="senderName"
                        type="text"
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        placeholder="Votre prénom"
                        className="pl-10 bg-gray-800 border-gray-700 text-white"
                        data-testid="sender-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-300">
                      {t('giftCard.personalMessage', 'Message personnel (optionnel)')}
                    </Label>
                    <div className="relative mt-1">
                      <Textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Joyeux anniversaire ! Fais-toi plaisir..."
                        rows={3}
                        className="bg-gray-800 border-gray-700 text-white resize-none"
                        data-testid="gift-message"
                      />
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 pt-4 border-t border-gray-800">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <Check className="w-4 h-4 text-green-400" />
                      <span>{t('giftCard.feature1', 'Envoyé par email instantanément')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <Check className="w-4 h-4 text-green-400" />
                      <span>{t('giftCard.feature2', 'Valide 1 an')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <Check className="w-4 h-4 text-green-400" />
                      <span>{t('giftCard.feature3', 'Utilisable en plusieurs fois')}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || finalAmount < 10}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg"
                    data-testid="purchase-gift-card"
                  >
                    {loading ? (
                      <span className="animate-pulse">{t('common.processing', 'Traitement...')}</span>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        {t('giftCard.purchase', 'Acheter la carte cadeau')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default GiftCardPage;
